import React, { useEffect, useState } from 'react';
import { useVibrancy } from '../VibrancyDashboardContext';
import { Avatar, HeaderIcon, cardHeaderStyle, cardStyle } from './shared';
import { VibrancyWatchlistService, WatchlistData } from '../vibrancyWatchlistService';
import { Period } from '../vibrancyDashboardService';

// ─── Constants ────────────────────────────────────────────────────────────────

const STAGE_COLORS: Record<string, { bg: string; color: string }> = {
    Identified:   { bg: '#ebf2ff', color: '#1b52c4' },
    Validated:    { bg: '#d1fae5', color: '#065f46' },
    Warmed:       { bg: '#fef3c7', color: '#92400e' },
    Engaged:      { bg: '#ede9fe', color: '#7c3aed' },
    Qualified:    { bg: '#ccfbf1', color: '#0f766e' },
    Contributing: { bg: '#dbeafe', color: '#0a1f44' },
};

const AVATAR_PALETTE = [
    { bg: '#ebf2ff', color: '#1b52c4' },
    { bg: '#fef3c7', color: '#92400e' },
    { bg: '#d1fae5', color: '#065f46' },
    { bg: '#cffafe', color: '#0891b2' },
    { bg: '#ede9fe', color: '#7c3aed' },
];

const EMPTY_DATA: WatchlistData = {
    signals: { total: 0, prevTotal: 0, signalLayer: 0, strategicLayer: 0, newSignals: 0, alumni: [] },
    advancements: { total: 0, prevTotal: 0, stages: [], qualifiedContributing: 0, alumni: [] },
};

// ─── Small helpers ────────────────────────────────────────────────────────────

const StagePill: React.FC<{ stage: string }> = ({ stage }) => {
    const c = STAGE_COLORS[stage] || { bg: '#eee', color: '#333' };
    return <span className="badge ms-1 font-11 fw-medium" style={{ background: c.bg, color: c.color }}>{stage}</span>;
};

function getInitials(name: string) {
    return (name || 'A').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

const SkeletonRows: React.FC = () => (
    <>
        {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className={`d-flex align-items-center gap-2 py-2 placeholder-glow ${i < 4 ? 'border-bottom' : ''}`} style={{ borderColor: '#f1f4fb' }}>
                <span className="placeholder rounded-circle flex-shrink-0" style={{ width: 30, height: 30, display: 'block' }} />
                <div className="flex-grow-1">
                    <span className="placeholder col-6 d-block mb-1" style={{ height: 12 }} />
                    <span className="placeholder col-4 d-block" style={{ height: 10 }} />
                </div>
            </div>
        ))}
    </>
);

// ─── Component ────────────────────────────────────────────────────────────────

/** VD-04 — Watchlist Signals Captured + Watchlist Advancements. */
const WatchlistCards: React.FC<{ period?: Period }> = ({ period }) => {
    const { version, selectedInstituteId, instituteLoaded, timeframe: globalTimeframe, compareMode } = useVibrancy();
    const timeframe = period ?? globalTimeframe;   // section period overrides the global one

    const [watchlist, setWatchlist] = useState<WatchlistData>(EMPTY_DATA);
    const [loading, setLoading]     = useState(false);

    useEffect(() => {
        if (!selectedInstituteId) { setWatchlist(EMPTY_DATA); return; }
        setLoading(true);
        VibrancyWatchlistService.getWatchlistData(selectedInstituteId, timeframe)
            .then(r  => setWatchlist(r.data?.data || EMPTY_DATA))
            .catch(() => setWatchlist(EMPTY_DATA))
            .finally(() => setLoading(false));
    }, [selectedInstituteId, timeframe]);

    if (!instituteLoaded) return null;

    const { signals, advancements } = watchlist;
    const watchlistLabel = version === 'sm' ? 'Watchlist Engine' : 'Watchlist';

    return (
        <div className="row g-3">

            {/* ── Signals Card ─────────────────────────────────────────────── */}
            <div className="col-md-6">
                <div className="card h-100" style={cardStyle}>
                    <div className="card-header bg-white d-flex justify-content-between align-items-center" style={cardHeaderStyle}>
                        <div className="fw-medium d-flex align-items-center gap-2 font-14">
                            <HeaderIcon icon="🔍" bg="#ebf2ff" color="#1b52c4" />Watchlist Signals Captured
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span className="font-13 text-secondary">New alumni flagged this period</span>
                            <span className="badge border font-11" style={{ background: '#ebf2ff', color: '#1b52c4', borderColor: '#c5d9f7' }}>
                                {signals.total} TOTAL
                            </span>
                        </div>
                    </div>

                    {/* Summary strip */}
                    <div className="row g-0 border-bottom" style={{ borderColor: '#e1e8f5' }}>
                        <div className="col-4 text-center py-2 border-end" style={{ borderColor: '#e1e8f5' }}>
                            <div className="fw-bold font-20 text-dark">{signals.total}</div>
                            <div className="text-uppercase fw-medium font-11 text-secondary">Action Count</div>
                            {compareMode && (
                                <div style={{ fontSize: 10.5 }}>
                                    <span className="fw-bold" style={{ color: '#4a5a80' }}>{signals.prevTotal}</span>
                                    <span className="fw-bold ms-1" style={{ color: signals.total >= signals.prevTotal ? '#0f9d58' : '#dc3545' }}>
                                        {signals.total >= signals.prevTotal ? '↑' : '↓'} {Math.abs(signals.total - signals.prevTotal)}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="col-4 text-center py-2 border-end bg-primary bg-opacity-10" style={{ borderColor: '#e1e8f5' }}>
                            <div className="fw-bold font-20 text-primary">{signals.signalLayer}</div>
                            <div className="text-uppercase fw-medium font-11 text-secondary mt-1">Signal Layer</div>
                        </div>
                        <div className="col-4 text-center py-2 bg-bs-purple bg-opacity-10">
                            <div className="fw-bold font-20 text-purple">{signals.strategicLayer}</div>
                            <div className="text-uppercase fw-medium font-11 text-secondary">Strategic Layer</div>
                        </div>
                    </div>

                    <div className="card-body py-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="text-uppercase fw-medium font-12 text-secondary">Top Alumni Flagged</span>
                            <span className="text-uppercase fw-bold font-11" style={{ color: '#8fa0c0' }}>Actions</span>
                        </div>
                        {loading ? (
                            <SkeletonRows />
                        ) : signals.alumni.length === 0 ? (
                            <div className="text-muted small py-3 text-center">No alumni were flagged this period</div>
                        ) : (
                            signals.alumni.map((a, i) => {
                                const av = AVATAR_PALETTE[i % AVATAR_PALETTE.length];
                                return (
                                    <div key={a.studentId || i}
                                        className={`d-flex align-items-center gap-2 py-2 ${i < signals.alumni.length - 1 ? 'border-bottom' : ''}`}
                                        style={{ borderColor: '#f1f4fb' }}>
                                        <div className="text-center fw-bold flex-shrink-0" style={{ fontSize: 13, color: '#8fa0c0', minWidth: 22 }}>
                                            {i + 1}
                                        </div>
                                        <Avatar initials={getInitials(a.name)} bg={av.bg} color={av.color} />
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                            <div className="fw-medium font-14 text-truncate">{a.name}
                                                <span className="badge ms-1 font-11 fw-medium"
                                                    style={a.layer === 'Strategic'
                                                        ? { background: '#ede9fe', color: '#7c3aed' }
                                                        : { background: '#ebf2ff', color: '#1b52c4' }}>
                                                    {a.layer}
                                                </span>
                                            </div>
                                            <div className="font-11 text-secondary">
                                                {a.passOutYear ? `Class of ${a.passOutYear}` : 'Alumni'} · {a.reason}
                                            </div>
                                        </div>
                                        <div className="text-end flex-shrink-0">
                                            <div className="fw-bold" style={{ fontSize: 16, color: '#0a1f44' }}>
                                                {/* {a.actionCount.toLocaleString()} */}
                                                 {(a.actionCount ?? 0).toLocaleString()}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <div className="px-3 py-2 border-top" style={{ background: '#f4f6fb', borderColor: '#e1e8f5' }}>
                        <a href={`${process.env.REACT_APP_APP_BASE_URL}/support/watchlist-report?institute_id=${selectedInstituteId}&direction=forward`}
                            target="_blank" rel="noopener noreferrer"
                            className="text-decoration-none fw-medium font-14 text-primary">
                            View all signals in {watchlistLabel} →
                        </a>
                    </div>
                </div>
            </div>

            {/* ── Advancements Card ─────────────────────────────────────────── */}
            <div className="col-md-6">
                <div className="card h-100" style={cardStyle}>
                    <div className="card-header bg-white d-flex justify-content-between align-items-center" style={cardHeaderStyle}>
                        <div className="fw-medium d-flex align-items-center gap-2 font-14">
                            <HeaderIcon icon="🚀" bg="#d1fae5" color="#0f9d58" />Watchlist Advancements
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span className="font-13 text-secondary">Pipeline stage movements this period</span>
                            <span className="badge border font-11" style={{ background: '#d1fae5', color: '#065f46', borderColor: '#a7f3d0' }}>
                                {advancements.total} TOTAL
                            </span>
                        </div>
                    </div>

                    {/* Summary strip */}
                    <div className="row g-0 border-bottom" style={{ borderColor: '#e1e8f5' }}>
                        <div className="col-4 text-center py-2 border-end" style={{ borderColor: '#e1e8f5' }}>
                            <div className="fw-bold font-20 text-dark">{advancements.total}</div>
                            <div className="text-uppercase fw-medium font-11 text-secondary mt-1">Advancements</div>
                            {compareMode && (
                                <div className="mt-1" style={{ fontSize: 10.5 }}>
                                    <span className="fw-bold" style={{ color: '#4a5a80' }}>{advancements.prevTotal}</span>
                                    <span className="fw-bold ms-1" style={{ color: advancements.total >= advancements.prevTotal ? '#0f9d58' : '#dc3545' }}>
                                        {advancements.total >= advancements.prevTotal ? '↑' : '↓'} {Math.abs(advancements.total - advancements.prevTotal)}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="col-4 py-2 px-2 d-flex flex-column justify-content-center bg-success bg-opacity-10 border-end font-12 text-secondary gap-2"
                            style={{ borderColor: '#e1e8f5' }}>
                            {advancements.stages.map((s, i) => (
                                <div key={i}><span className="fw-bold" style={{ color: '#0a1f44' }}>{s.count}</span> {s.label}</div>
                            ))}
                        </div>
                        <div className="col-4 text-center py-2 d-flex flex-column align-items-center justify-content-center">
                            <div className="fw-bold" style={{ fontSize: 20, color: '#f59e0b' }}>{advancements.qualifiedContributing}</div>
                            <div className="text-uppercase fw-medium font-11 text-secondary mt-1">Qualified / Contributing</div>
                        </div>
                    </div>

                    <div className="card-body py-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="text-uppercase fw-medium font-12 text-secondary">Alumni Who Advanced</span>
                            <span className="text-uppercase fw-bold font-11" style={{ color: '#8fa0c0' }}>Actions</span>
                        </div>
                        {loading ? (
                            <SkeletonRows />
                        ) : advancements.alumni.length === 0 ? (
                            <div className="text-muted small py-3 text-center">No stage advancements this period</div>
                        ) : (
                            advancements.alumni.map((a, i) => {
                                const av = AVATAR_PALETTE[i % AVATAR_PALETTE.length];
                                return (
                                    <div key={a.studentId || i}
                                        className={`d-flex align-items-center gap-2 py-2 ${i < advancements.alumni.length - 1 ? 'border-bottom' : ''}`}
                                        style={{ borderColor: '#f1f4fb' }}>
                                        <div className="text-center fw-bold flex-shrink-0" style={{ fontSize: 13, color: '#8fa0c0', minWidth: 22 }}>
                                            {i + 1}
                                        </div>
                                        <Avatar initials={getInitials(a.name)} bg={av.bg} color={av.color} />
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                            <div className="fw-medium font-14 text-truncate">{a.name}</div>
                                            <div className="d-flex align-items-center gap-1 mt-1 flex-wrap" style={{ fontSize: 10.5, color: '#8fa0c0' }}>
                                                {a.passOutYear ? `Class of ${a.passOutYear}` : 'Alumni'}
                                                <StagePill stage={a.fromStage} />
                                                <span style={{ color: '#8fa0c0' }}>→</span>
                                                <StagePill stage={a.toStage} />
                                            </div>
                                        </div>
                                        <div className="text-end flex-shrink-0">
                                            <div className="fw-bold" style={{ fontSize: 16, color: '#0a1f44' }}>
                                                {/* {a.actionCount.toLocaleString()} */}
                                                {(a.actionCount ?? 0).toLocaleString()}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <div className="px-3 py-2 border-top" style={{ background: '#f4f6fb', borderColor: '#e1e8f5' }}>
                        <a href={`${process.env.REACT_APP_APP_BASE_URL}/support/watchlist-report?institute_id=${selectedInstituteId}&layer=activation&direction=forward`}
                            target="_blank" rel="noopener noreferrer"
                            className="text-decoration-none fw-medium font-14 text-primary">
                            View all advancements in {watchlistLabel} →
                        </a>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default WatchlistCards;
